/**
 * Iter 67x — Loyalty-redeem widget for BookingWidget.
 *
 * Renders a compact card that:
 *   • Calls /api/customer/loyalty (snapshot) once on mount to get balance + tier.
 *   • Quotes the discount available against the current `rentalTotal` via
 *     /api/customer/loyalty/redeem-preview. Re-quotes whenever rentalTotal
 *     changes so the displayed €X off stays accurate.
 *   • Renter clicks "Apply max" → parent BookingWidget receives the points
 *     count via `onChange`. Backend will re-validate + clamp the same numbers
 *     server-side so a tampered client can't cheat.
 *   • Hidden entirely when the user isn't logged in / has 0 points.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Sparkles, Coins, BadgePercent, X } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function BookingLoyaltyRedeem({ rentalTotal = 0, value = 0, onChange, testId = "booking-loyalty-redeem" }) {
  const [snap, setSnap] = useState(null);
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState(false);

  // Pull the customer's loyalty snapshot once. If it 401s (guest checkout) or
  // the user has zero balance we render nothing.
  useEffect(() => {
    let cancelled = false;
    api.get("/customer/loyalty")
      .then((r) => { if (!cancelled) setSnap(r.data); })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, []);

  // Re-quote the discount whenever rentalTotal changes (multi-day picker
  // updates, addon toggles, etc.). Server is authoritative; we just show a
  // preview for UX.
  useEffect(() => {
    if (!snap || (snap.points || 0) <= 0 || rentalTotal <= 0) return;
    let cancelled = false;
    api.post("/customer/loyalty/redeem-preview", {
      points: snap.points,        // ask for the maximum and let the server clamp
      rental_total: rentalTotal,
    })
      .then((r) => { if (!cancelled) setQuote(r.data); })
      .catch(() => { if (!cancelled) setQuote(null); });
    return () => { cancelled = true; };
  }, [snap, rentalTotal]);

  if (error || !snap || (snap.points || 0) <= 0) return null;

  const maxRedeemablePts = quote?.points_to_spend || 0;
  const maxDiscountEur = quote?.discount_eur || 0;
  const applied = Number(value || 0) > 0;
  const appliedEur = applied ? Math.round((value / 100) * 100) / 100 : 0; // 100 pts = €1

  return (
    <div
      className={`mt-3 rounded-2xl border-2 px-4 py-3 transition ${
        applied
          ? "bg-gradient-to-br from-amber-50/80 via-orange-50/60 to-yellow-50/40 border-amber-300 shadow-sm shadow-amber-100"
          : "bg-gradient-to-br from-amber-50/30 via-white to-yellow-50/20 border-amber-100"
      }`}
      data-testid={testId}
    >
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl bg-amber-100 ring-1 ring-amber-200 flex items-center justify-center shrink-0">
          <Sparkles className="h-5 w-5 text-amber-600" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <div className="text-sm font-bold text-neutral-900">
              {applied ? `−€${appliedEur.toFixed(2)} loyalty discount applied` : "Redeem loyalty points"}
            </div>
            <span className="text-[10px] uppercase tracking-wider font-bold bg-amber-100 text-amber-800 border border-amber-200 px-1.5 py-0.5 rounded-full">
              {snap.tier?.label || "Member"}
            </span>
          </div>
          <div className="text-xs text-neutral-600 mt-0.5">
            <Coins className="inline h-3 w-3 text-amber-500 mr-0.5" />
            You have <strong>{snap.points.toLocaleString()}</strong> pts
            {maxRedeemablePts > 0 && (
              <>
                {" "}— use up to{" "}
                <strong>{maxRedeemablePts.toLocaleString()}</strong> on this trip ({" "}
                <strong>€{maxDiscountEur.toFixed(2)}</strong> off — capped at {Math.round((snap.redeem_cap_pct || 0.2) * 100)}% of rental).
              </>
            )}
          </div>
          {maxRedeemablePts > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {!applied ? (
                <Button
                  type="button"
                  onClick={() => onChange && onChange(maxRedeemablePts)}
                  size="sm"
                  className="h-8 px-3.5 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white text-xs font-semibold shadow-sm"
                  data-testid={`${testId}-apply-btn`}
                >
                  <BadgePercent className="h-3.5 w-3.5 mr-1.5" />
                  Apply €{maxDiscountEur.toFixed(2)} off
                </Button>
              ) : (
                <Button
                  type="button"
                  onClick={() => onChange && onChange(0)}
                  size="sm"
                  variant="ghost"
                  className="h-8 px-3 rounded-full text-amber-700 hover:bg-amber-100 text-xs"
                  data-testid={`${testId}-remove-btn`}
                >
                  <X className="h-3.5 w-3.5 mr-1" />
                  Remove discount
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
